/**
 * Cart page + drawer: wishlist panel via entry button (not header tabs).
 */
(function () {
  const STORAGE_KEY = 'onhow-wishlist';

  function getWishlist() {
    try {
      const items = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(items) ? items : [];
    } catch {
      return [];
    }
  }

  function saveWishlist(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    if (window.onhowWishlist && typeof window.onhowWishlist.updateWishlistCount === 'function') {
      window.onhowWishlist.updateWishlistCount();
    }
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escapeAttr(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;');
  }

  function wishlistImageUrl(url) {
    if (!url) return '';
    let normalized = String(url).trim();
    if (normalized.startsWith('//')) normalized = 'https:' + normalized;
    normalized = normalized.replace(
      /_(pico|icon|thumb|small|compact|medium|large|grande|original|master)(\.(jpg|jpeg|png|webp|gif))/i,
      '_400x$2'
    );
    if (normalized.includes('cdn.shopify.com') && !/[?&]width=\d+/.test(normalized)) {
      normalized += (normalized.includes('?') ? '&' : '?') + 'width=400';
    }
    return normalized;
  }

  function getProductHandle(item) {
    let handle = String(item?.handle || '')
      .replace(/^\/+|\/+$/g, '')
      .replace(/^products\//, '');
    const url = String(item?.url || item?.productUrl || '');
    if (!handle && url) {
      const match = url.match(/\/products\/([^/?#]+)/);
      if (match) handle = match[1];
    }
    return handle;
  }

  function firstPurchasableVariant(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    return (
      variants.find((variant) => {
        if (!variant || variant.available === false) return false;
        const managed = Boolean(variant.inventory_management);
        const continueSelling = variant.inventory_policy === 'continue';
        const qty = Number(variant.inventory_quantity);
        if (managed && !continueSelling && Number.isFinite(qty) && qty <= 0) return false;
        return variant.available === true;
      }) || null
    );
  }

  function wishlistStatusMessage(status) {
    if (status === 'out_of_stock') return 'Out of stock';
    if (status === 'unavailable') return 'Product unavailable';
    return '';
  }

  function applyWishlistAvailability(row, status, variantId) {
    if (!row) return;
    const addBtn = row.querySelector('.cart-wishlist-item__add');
    const details = row.querySelector('.cart-wishlist-item__details');
    let note = row.querySelector('.cart-wishlist-item__status');
    if (!note && details) {
      note = document.createElement('p');
      note.className = 'cart-wishlist-item__status';
      details.appendChild(note);
    }

    if (status === 'ok') {
      row.classList.remove('is-unavailable');
      row.dataset.availability = 'ok';
      if (note) {
        note.hidden = true;
        note.textContent = '';
      }
      if (variantId) row.dataset.variantId = String(variantId);
      if (addBtn) {
        addBtn.disabled = false;
        addBtn.classList.remove('is-loading');
        addBtn.removeAttribute('aria-disabled');
      }
      return;
    }

    if (status === 'checking') {
      row.classList.remove('is-unavailable');
      row.dataset.availability = 'checking';
      if (note) {
        note.hidden = true;
        note.textContent = '';
      }
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.setAttribute('aria-disabled', 'true');
      }
      return;
    }

    const message = wishlistStatusMessage(status);
    row.classList.add('is-unavailable');
    row.dataset.availability = status;
    if (note) {
      note.hidden = false;
      note.textContent = message;
    }
    if (addBtn) {
      addBtn.disabled = true;
      addBtn.classList.remove('is-loading');
      addBtn.setAttribute('aria-disabled', 'true');
    }
  }

  function fetchWishlistProduct(item) {
    const handle = getProductHandle(item);
    if (!handle) return Promise.resolve({ missing: true });
    return fetch(`/products/${encodeURIComponent(handle)}.js`, { cache: 'no-store' }).then((res) => {
      if (res.status === 404) return { missing: true };
      if (!res.ok) throw new Error('unavailable');
      return res.json();
    });
  }

  function hydrateWishlistItem(row, item) {
    if (!row) return;
    applyWishlistAvailability(row, 'checking');
    const img = row.querySelector('img');
    fetchWishlistProduct({
      handle: item.handle || row.dataset.productHandle,
      url: item.url || row.dataset.productUrl,
    })
      .then((product) => {
        if (!product || product.missing) {
          applyWishlistAvailability(row, 'unavailable');
          img?.classList.add('is-missing');
          return;
        }

        const src = product.featured_image || product.images?.[0] || '';
        if (img && src) {
          img.src = wishlistImageUrl(src);
          img.classList.remove('is-missing');
        }

        const availableVariant = firstPurchasableVariant(product);
        if (!product.available || !availableVariant) {
          applyWishlistAvailability(row, 'out_of_stock');
          return;
        }

        applyWishlistAvailability(row, 'ok', availableVariant.id);
      })
      .catch(() => {
        applyWishlistAvailability(row, 'unavailable');
      });
  }

  function renderWishlistItemsMarkup(items) {
    return items
      .map((item) => {
        const title = item.title || '';
        const titleHtml = escapeHtml(title);
        const titleAttr = escapeAttr(title);
        const priceHtml = escapeHtml(item.price);
        const urlAttr = escapeAttr(item.url);
        const urlHtml = escapeHtml(item.url);
        const image = escapeAttr(wishlistImageUrl(item.image));
        const idAttr = escapeAttr(item.id || '');
        const handleAttr = escapeAttr(item.handle || '');

        return `
          <li class="cart-wishlist-item wishlist-item" data-product-id="${idAttr}" data-product-handle="${handleAttr}" data-product-title="${titleAttr}" data-product-url="${urlAttr}">
            <a href="${urlHtml}" class="cart-wishlist-item__media">
              <img src="${image}" alt="${titleHtml}" loading="lazy" decoding="async" width="112" height="149">
            </a>
            <div class="cart-wishlist-item__body">
              <div class="cart-wishlist-item__details">
                <a href="${urlHtml}" class="cart-wishlist-item__title">${titleHtml}</a>
                <p class="cart-wishlist-item__price">${priceHtml}</p>
                <p class="cart-wishlist-item__status" hidden></p>
              </div>
              <div class="cart-wishlist-item__actions">
                <button type="button" class="cart-wishlist-item__add add-to-cart-btn" disabled aria-disabled="true">Add to cart</button>
                <button type="button" class="cart-wishlist-item__remove" aria-label="Remove from wishlist">Remove</button>
              </div>
            </div>
          </li>
        `;
      })
      .join('');
  }

  class SkippWishlistView {
    constructor(config) {
      this.context = config.context;
      this.root = config.root;
      this.wishlistPanel = document.getElementById(config.wishlistPanelId);
      this.wishlistList = document.getElementById(config.wishlistListId);
      this.emptyEl = config.emptyElId ? document.getElementById(config.emptyElId) : null;
      this.cartContent = config.cartContentId ? document.getElementById(config.cartContentId) : null;
      this.cartPanel = config.cartPanelId ? document.getElementById(config.cartPanelId) : null;
      this.headerTitleEl = config.headerTitleId ? document.getElementById(config.headerTitleId) : null;
      this.cartWarnings = config.cartWarningsSelector
        ? this.root.querySelector(config.cartWarningsSelector)
        : null;
      this.activeView = 'cart';
      this.viewClass = config.viewClass;
      this.defaultHeaderTitle = config.defaultHeaderTitle || 'MY BAG';
      this.wishlistHeaderTitle = config.wishlistHeaderTitle || 'WISHLIST';

      if (!this.root || !this.wishlistPanel || !this.wishlistList) {
        this.ready = false;
        return;
      }

      this.ready = true;
      this.applyView('cart');
      this.updateEntryCounts();
    }

    syncHeaderTabs(isWishlist) {
      const bagTab = this.root.querySelector('[data-cart-show-bag]');
      const wishTab = this.root.querySelector('[data-cart-show-wishlist]');
      if (bagTab) {
        bagTab.classList.toggle('is-active', !isWishlist);
        bagTab.setAttribute('aria-selected', isWishlist ? 'false' : 'true');
      }
      if (wishTab) {
        wishTab.classList.toggle('is-active', isWishlist);
        wishTab.setAttribute('aria-selected', isWishlist ? 'true' : 'false');
      }
      if (this.headerTitleEl) {
        this.headerTitleEl.textContent = isWishlist ? this.wishlistHeaderTitle : this.defaultHeaderTitle;
      }
    }

    applyView(view) {
      if (!this.ready) return;

      const isWishlist = view === 'wishlist';
      this.activeView = isWishlist ? 'wishlist' : 'cart';

      this.root.classList.toggle(this.viewClass, isWishlist);
      this.syncHeaderTabs(isWishlist);

      if (this.context === 'page') {
        if (this.cartContent) {
          this.cartContent.classList.toggle('is-active', !isWishlist);
        }
        this.wishlistPanel.classList.toggle('is-active', isWishlist);
        this.wishlistPanel.setAttribute('aria-hidden', isWishlist ? 'false' : 'true');
      } else {
        if (this.cartPanel) {
          this.cartPanel.hidden = isWishlist;
        }
        this.wishlistPanel.hidden = !isWishlist;
        this.wishlistPanel.setAttribute('aria-hidden', isWishlist ? 'false' : 'true');
        this.wishlistPanel.classList.toggle('is-active', isWishlist);
      }

      if (this.cartWarnings) {
        this.cartWarnings.style.display = isWishlist ? 'none' : '';
      }

      if (isWishlist) {
        this.renderWishlist();
      }
    }

    showWishlist() {
      this.applyView('wishlist');
    }

    showCart() {
      this.applyView('cart');
    }

    renderWishlist() {
      if (!this.ready) return;

      const wishlistItems = getWishlist();

      if (wishlistItems.length === 0) {
        this.wishlistList.innerHTML = '';
        this.wishlistList.hidden = true;
        if (this.emptyEl) {
          this.emptyEl.hidden = false;
        }
        return;
      }

      if (this.emptyEl) {
        this.emptyEl.hidden = true;
      }
      this.wishlistList.hidden = false;
      this.wishlistList.innerHTML = renderWishlistItemsMarkup(wishlistItems);
      this.wishlistList.querySelectorAll('.cart-wishlist-item').forEach((row, index) => {
        hydrateWishlistItem(row, wishlistItems[index] || {});
      });
    }

    updateEntryCounts() {
      const count = getWishlist().length;
      this.root.querySelectorAll('.cart-wishlist-entry__count, .skipp-cart-header-tabs__count').forEach((el) => {
        el.textContent = count > 0 ? String(count) : '';
      });
    }

    removeItem(productTitle, productId) {
      const wishlistItems = getWishlist().filter((item) => {
        if (productId && item.id && String(item.id) === String(productId)) return false;
        return item.title !== productTitle;
      });
      saveWishlist(wishlistItems);
      this.renderWishlist();
      this.updateEntryCounts();
      notifyWishlistChanged();
    }

    refreshCartAfterAdd() {
      if (this.context === 'drawer') {
        const drawerItems = document.querySelector('cart-drawer-items');
        if (drawerItems && typeof drawerItems.refreshDrawerFromServer === 'function') {
          return drawerItems.refreshDrawerFromServer();
        }
        return Promise.resolve();
      }

      const cartItemsEl = document.querySelector('cart-items');
      if (cartItemsEl && typeof cartItemsEl.onCartUpdate === 'function') {
        return cartItemsEl.onCartUpdate();
      }
      return Promise.resolve();
    }

    addToCart(button, productUrl, productTitle, productId, row) {
      if (!button || button.disabled || row?.dataset.availability !== 'ok') return;
      if (row?.classList.contains('is-unavailable')) return;

      const markUnavailable = (status) => {
        applyWishlistAvailability(row, status === 'out_of_stock' ? 'out_of_stock' : 'unavailable');
      };

      const addVariant = (variantId) => {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');

        if (this.context === 'drawer') {
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
            formData.append(
              'sections',
              cartDrawer.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            if (typeof cartDrawer.setActiveElement === 'function') {
              cartDrawer.setActiveElement(button);
            }
          }
        }

        const config =
          typeof fetchConfig === 'function'
            ? fetchConfig('javascript')
            : { method: 'POST', headers: { Accept: 'application/javascript' } };
        config.headers = config.headers || {};
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        const addUrl = window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add';
        return fetch(addUrl, config).then((response) => response.json());
      };

      const finishAdd = (data) => {
        if (data.status) {
          const text = `${data.message || ''} ${data.description || ''}`.toLowerCase();
          if (text.includes('sold out') || text.includes('out of stock') || data.status === 422) {
            const err = new Error('out_of_stock');
            err.skippStatus = 'out_of_stock';
            throw err;
          }
          const err = new Error('unavailable');
          err.skippStatus = 'unavailable';
          throw err;
        }

        const wishlistItems = getWishlist().filter((item) => {
          if (productId && item.id && String(item.id) === String(productId)) return false;
          return item.title !== productTitle;
        });
        saveWishlist(wishlistItems);
        notifyWishlistChanged();

        if (this.context === 'drawer' && data.sections && data.sections['cart-drawer']) {
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(data);
          }
        } else {
          return this.refreshCartAfterAdd();
        }
      };

      button.disabled = true;
      button.classList.add('is-loading');

      const start =
        row?.dataset.variantId
          ? Promise.resolve(row.dataset.variantId)
          : fetchWishlistProduct({
              handle: row?.dataset.productHandle,
              url: productUrl,
            }).then((product) => {
              if (!product || product.missing) {
                const err = new Error('unavailable');
                err.skippStatus = 'unavailable';
                throw err;
              }
              const availableVariant = (product.variants || []).find((variant) => variant.available);
              if (!product.available || !availableVariant) {
                const err = new Error('out_of_stock');
                err.skippStatus = 'out_of_stock';
                throw err;
              }
              row.dataset.variantId = String(availableVariant.id);
              return availableVariant.id;
            });

      start
        .then((variantId) => addVariant(variantId))
        .then((data) => finishAdd(data))
        .then(() => {
          reattachViews();
          document.dispatchEvent(new CustomEvent('skipp:cart-ui-refreshed'));
          if (pageView) pageView.showCart();
          if (drawerView) drawerView.showCart();
        })
        .catch((error) => {
          markUnavailable(error?.skippStatus || 'unavailable');
        })
        .finally(() => {
          button.classList.remove('is-loading');
          if (!row?.classList.contains('is-unavailable')) {
            button.disabled = false;
          }
        });
    }

    addRecToCart(button) {
      const variantId = button.dataset.variantId;
      if (!variantId || button.disabled) return;

      button.disabled = true;
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      const config =
        typeof fetchConfig === 'function'
          ? fetchConfig('javascript')
          : { method: 'POST', headers: { Accept: 'application/javascript' } };
      config.headers = config.headers || {};
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      const addUrl = window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add';

      fetch(addUrl, config)
        .then((response) => response.json())
        .then((data) => {
          if (data.status) throw new Error(data.description || data.message || 'Unable to add to cart');
          button.textContent = 'Added';
          return this.refreshCartAfterAdd();
        })
        .then(() => {
          reattachViews();
          document.dispatchEvent(new CustomEvent('skipp:cart-ui-refreshed'));
          if (pageView) pageView.showCart();
          if (drawerView) drawerView.showCart();
        })
        .catch((error) => {
        })
        .finally(() => {
          setTimeout(() => {
            button.disabled = false;
            if (button.textContent === 'Added') {
              button.textContent = button.dataset.defaultLabel || 'Add to bag';
            }
          }, 1800);
        });
    }

    onListClick(event) {
      const addBtn = event.target.closest('.cart-wishlist-item__add');
      if (addBtn) {
        const row = addBtn.closest('.cart-wishlist-item');
        if (!row || addBtn.disabled || row.classList.contains('is-unavailable') || row.dataset.availability !== 'ok') return;
        event.preventDefault();
        this.addToCart(addBtn, row.dataset.productUrl, row.dataset.productTitle, row.dataset.productId, row);
        return;
      }

      const removeBtn = event.target.closest('.cart-wishlist-item__remove');
      if (removeBtn) {
        const row = removeBtn.closest('.cart-wishlist-item');
        if (!row) return;
        event.preventDefault();
        this.removeItem(row.dataset.productTitle, row.dataset.productId);
      }
    }
  }

  let pageView = null;
  let drawerView = null;
  let listenersBound = false;

  function notifyWishlistChanged() {
    document.dispatchEvent(new CustomEvent('skipp:wishlist-updated'));
  }

  function syncAllViews() {
    if (pageView && pageView.ready) {
      pageView.updateEntryCounts();
      if (pageView.activeView === 'wishlist') pageView.renderWishlist();
    }
    if (drawerView && drawerView.ready) {
      drawerView.updateEntryCounts();
      if (drawerView.activeView === 'wishlist') drawerView.renderWishlist();
    }
  }

  function reattachViews() {
    const pageViewState = pageView ? pageView.activeView : 'cart';
    const drawerViewState = drawerView ? drawerView.activeView : 'cart';

    pageView = null;
    drawerView = null;
    initViews();

    if (pageView && pageViewState === 'wishlist') {
      pageView.showWishlist();
    }
    if (drawerView && drawerViewState === 'wishlist') {
      drawerView.showWishlist();
    }
  }

  function initViews() {
    const cartItems = document.querySelector('cart-items');
    if (cartItems && document.getElementById('cart-wishlist-content')) {
      pageView = new SkippWishlistView({
        context: 'page',
        root: cartItems,
        wishlistPanelId: 'cart-wishlist-content',
        wishlistListId: 'cart-wishlist-list',
        emptyElId: 'cart-wishlist-empty',
        cartContentId: 'cart-content',
        cartWarningsSelector: '.cart__warnings',
        viewClass: 'cart-items--wishlist-view',
      });
    }

    const drawer = document.querySelector('cart-drawer');
    if (drawer && document.getElementById('cart-drawer-wishlist-content')) {
      drawerView = new SkippWishlistView({
        context: 'drawer',
        root: drawer,
        wishlistPanelId: 'cart-drawer-wishlist-content',
        wishlistListId: 'cart-drawer-wishlist-list',
        emptyElId: 'cart-drawer-wishlist-empty',
        cartPanelId: 'cart-drawer-cart-panel',
        headerTitleId: 'cart-drawer-heading',
        viewClass: 'cart-drawer--wishlist-view',
      });
    }
  }

  function viewsNeedReattach() {
    if (document.getElementById('cart-drawer-wishlist-content') && (!drawerView?.ready || !drawerView.wishlistPanel?.isConnected)) {
      return true;
    }
    if (document.getElementById('cart-wishlist-content') && (!pageView?.ready || !pageView.wishlistPanel?.isConnected)) {
      return true;
    }
    return false;
  }

  function bindGlobalListeners() {
    if (listenersBound) return;
    listenersBound = true;

    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-show-wishlist]')) {
        event.preventDefault();
        if (pageView && pageView.ready) pageView.showWishlist();
        if (drawerView && drawerView.ready) drawerView.showWishlist();
        return;
      }

      if (event.target.closest('[data-cart-show-bag]')) {
        event.preventDefault();
        if (pageView && pageView.ready) pageView.showCart();
        if (drawerView && drawerView.ready) drawerView.showCart();
        return;
      }

      const recAdd = event.target.closest('[data-wishlist-rec-add]');
      if (recAdd && pageView && pageView.ready && pageView.activeView === 'wishlist') {
        event.preventDefault();
        pageView.addRecToCart(recAdd);
        return;
      }

      if (event.target.closest('#cart-wishlist-list') && pageView && pageView.ready) {
        pageView.onListClick(event);
        return;
      }

      if (event.target.closest('#cart-drawer-wishlist-list') && drawerView && drawerView.ready) {
        drawerView.onListClick(event);
      }
    });

    document.addEventListener('skipp:wishlist-updated', syncAllViews);

    document.addEventListener('skipp:show-wishlist-panel', () => {
      if (pageView && pageView.ready) pageView.showWishlist();
      if (drawerView && drawerView.ready) drawerView.showWishlist();
    });

    let reattachTimer = null;
    function scheduleCartUiRefresh() {
      clearTimeout(reattachTimer);
      reattachTimer = setTimeout(() => {
        if (viewsNeedReattach()) {
          reattachViews();
        } else {
          syncAllViews();
        }
      }, 80);
    }

    document.addEventListener('skipp:cart-ui-refreshed', scheduleCartUiRefresh);

    document.addEventListener('cart:updated', () => {
      scheduleCartUiRefresh();
    });
  }

  bindGlobalListeners();
  document.addEventListener('DOMContentLoaded', initViews);
  if (document.readyState !== 'loading') {
    initViews();
  }
})();
